import { useMemo, useState, type ReactNode } from "react";
import { formatNotBefore, setDone } from "../../tasks/earlyDone";
import { notBeforeMs } from "../../core/notYet";
import { useTranslation } from "react-i18next";
import { Check, MoreHorizontal, Timer } from "lucide-react";
import { clsx } from "clsx";
import { registerViewType, type ViewProps } from "../../registry/viewTypes";
import { useDocSelector } from "../../core/useDocSelector";
import { selectBin, energyOf, type BinConfig, type BinRow } from "../../core/bins";
import { snapshotOf, skipRecurring } from "../../core/ops";
import { NotNowChoices, NotNowQuestion } from "../../tasks/NotNow";
import { shouldAsk } from "../../tasks/notNowLogic";
import { wantDisplay, wantLevel } from "../../core/want";
import { groupsOf } from "../../core/structure";
import { round1 } from "../../core/format";
import { useDevice } from "../../app/device";
import { EnergyPips } from "../../ui/EnergyPips";
import { Floating, Why, usePopover } from "../../ui";
import { FILTER_SCHEMA } from "../shared";
import { WantBar } from "../../tasks/WantBar";
import { startTracking } from "../../tracking/timelog";
import { STARTER_MINUTES, useStarter } from "../../tracking/starter";
import { useDeferredValue } from "../../app/useDeferredValue";
import { useCircle } from "../../circle/useCircle";
import { matchesFor, rotationOf, turn } from "../../core/circle";
import { useSearchParams } from "react-router";
import { withElsewhere } from "../elsewhere/withElsewhere";
import { SOURCE_SCHEMA, type Source } from "../elsewhere/source";
import { useSignalContext } from "../../signals/useSignalContext";
import { FitMarker } from "../../signals/FitMarker";
import { formatPeriod } from "../../tasks/period";
import { useMuteBadges } from "../../app/useMuteBadges";
import { EnergyControl } from "../../app/EnergyControl";
import { ForControl } from "../../circle/ForControl";

/**
 * The ADHD "what wants doing" list (spec §9.7 `bin`, §9.19): want desc, energy asc; nextAction in
 * place of the title when set; energy pips; group colour with its word; why? on every want.
 * No-surprises (§9.19): the order is frozen while the pointer is inside the widget.
 */
type BinViewConfig = BinConfig & { source?: Source; for?: string };

function BinView({ config, ctx, title, quietEmpty }: ViewProps<BinViewConfig>) {
  const mute = useMuteBadges();
  const { t, i18n } = useTranslation();
  const deviceEnergy = useDevice((s) => s.energy);
  const deviceFor = useDevice((s) => s.forWhom);
  const circle = useCircle(ctx.handle);
  const [params] = useSearchParams();
  // kiosks show everyone's tasks by default (spec §9.29)
  const forWhom = config.for ?? (params.get("kiosk") === "1" ? "anyone" : deviceFor);
  const snapshot = useDocSelector(ctx.handle, (doc) => snapshotOf(doc));
  const groups =
    useDocSelector(
      ctx.handle,
      (doc) => doc.getMap("groups").toJSON() as Record<string, { title: string; color: string | null }>,
    ) ?? {};
  // `now` ticks every second; want changes so slowly that a minute is enough for the order
  const minute = Math.floor(ctx.now / 60_000);
  const nowMs = minute * 60_000;
  const { shared, me, memberIds } = circle;
  const memberKey = memberIds.join(",");
  const sig = useSignalContext(ctx.handle, ctx.workspaceId, nowMs);
  const liveRows = useMemo(() => {
    const rows = snapshot ? selectBin(snapshot, config, nowMs, deviceEnergy, sig.signals, sig.rules) : [];
    if (!shared || !me) return rows;
    const members = memberKey ? memberKey.split(",") : [];
    return rows.filter((r) => matchesFor(r.task, forWhom, me, { shared: true, members }));
  }, [snapshot, config, deviceEnergy, nowMs, shared, me, memberKey, forWhom, sig.signals, sig.rules]);
  const {
    value: rows,
    pendingCount,
    apply,
    hoverProps,
  } = useDeferredValue(liveRows, (a, b) => a.map((r) => r.task.id).join() === b.map((r) => r.task.id).join());

  // set aside (*Not now*): out of the list, but never out of sight — one line says how many and when they come back
  const aside = useMemo(() => {
    if (!snapshot) return { n: 0, first: null as number | null };
    let n = 0;
    let first: number | null = null;
    for (const task of Object.values(snapshot.tasks)) {
      const props = (task.props ?? {}) as Record<string, unknown>;
      if (task.doneAt || task.archivedAt || typeof props["notNow.count"] !== "number") continue;
      const at = notBeforeMs(task as never, nowMs);
      if (at === null) continue;
      n++;
      if (first === null || at < first) first = at;
    }
    return { n, first };
  }, [snapshot, nowMs]);

  if (quietEmpty && rows.length === 0) return null;
  // the filters that apply to this widget sit in it (they were in the top bar); a section of another workspace
  // (`title`) and a wall display (kiosk) leave them out
  const controls = !title && params.get("kiosk") !== "1" && !config.for;
  return (
    <div className="flex flex-col gap-2" data-testid="bin-view" {...hoverProps}>
      {(title || pendingCount > 0 || controls) && (
        <div className="flex flex-wrap items-center gap-2">
          {controls && <EnergyControl />}
          {controls && shared && <ForControl />}
          {title && (
            <h2 className="text-xs font-semibold uppercase tracking-[0.06em] text-neutral-500">{title}</h2>
          )}
          {pendingCount > 0 && (
            <button
              type="button"
              className="kss-pill border-neutral-200 bg-neutral-100 text-neutral-700 hover:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-200"
              onClick={apply}
              data-testid="bin-apply-changes"
            >
              {mute ? t("bin.changesNoCount") : t("bin.changes", { n: pendingCount })}
            </button>
          )}
        </div>
      )}
      {rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-neutral-300 bg-[var(--surface-2)] px-6 py-10 text-center text-neutral-500 dark:border-neutral-700">
          <p>{t("bin.empty")}</p>
          {deviceEnergy !== null && (
            <p className="mt-1 text-[12.5px]">{t("bin.emptyEnergyHint", { n: deviceEnergy })}</p>
          )}
        </div>
      ) : (
        <ul
          className="kss-surface divide-y divide-neutral-200 dark:divide-neutral-800"
          data-testid="bin-list"
        >
          {rows.map((row) => (
            <BinRowView
              key={row.task.id}
              row={row}
              ctx={ctx}
              groupIds={snapshot ? groupsOf(snapshot, row.task.id) : []}
              groups={groups}
              onChanged={apply}
              turnOf={shared ? turn(row.task, memberIds) : null}
              rotating={shared && rotationOf(row.task, memberIds).length > 0}
              nameOf={circle.nameOf}
              me={me}
              shared={shared}
              fitMarker={
                <FitMarker
                  fit={row.fit}
                  reasons={row.reasons}
                  signals={sig.signals}
                  definitions={sig.definitions}
                  nowMs={nowMs}
                />
              }
            />
          ))}
        </ul>
      )}
      {aside.n > 0 && aside.first !== null && !title && (
        <p className="px-1 text-[12.5px] text-neutral-500" data-testid="bin-set-aside">
          {mute
            ? t("notNow.asideNoCount", { when: formatNotBefore(aside.first, i18n.language) })
            : t("notNow.aside", { n: aside.n, when: formatNotBefore(aside.first, i18n.language) })}
        </p>
      )}
    </div>
  );
}

function BinRowView({
  row,
  ctx,
  groupIds,
  groups,
  onChanged,
  turnOf,
  rotating,
  nameOf,
  me,
  shared,
  fitMarker,
}: {
  fitMarker?: ReactNode;
  row: BinRow;
  ctx: ViewProps<BinConfig>["ctx"];
  groupIds: string[];
  groups: Record<string, { title: string; color: string | null }>;
  onChanged(): void;
  turnOf: string | null;
  rotating: boolean;
  nameOf(id: string | null): string;
  me: string | null;
  shared: boolean;
}) {
  const { t, i18n } = useTranslation();
  const { task, computed } = row;
  const isRecurring = task.typeId === "recurring";
  const want = computed.want as number;
  const nextAction = typeof task.props.nextAction === "string" ? task.props.nextAction : "";
  const [justDone, setJustDone] = useState(false);
  const [asking, setAsking] = useState(false);
  const arm = useStarter((s) => s.arm);
  const {
    open: menuOpen,
    toggle: toggleMenu,
    setOpen: setMenuOpen,
    ref: menuRef,
    panelRef: menuPanel,
  } = usePopover<HTMLDivElement>();
  const ro = ctx.readOnly;

  const complete = () => {
    if (!setDone(ctx.write, t, i18n.language, task, true)) return;
    setJustDone(true);
    onChanged();
  };

  return (
    <li
      className={clsx("flex items-center gap-3 px-4 py-2.5 transition", justDone && "opacity-50")}
      data-testid="bin-row"
      data-task-id={task.id}
    >
      <button
        type="button"
        aria-label={t("task.done")}
        disabled={ro}
        onClick={complete}
        className="kss-control grid h-[22px] w-[22px] shrink-0 place-items-center rounded-[6px] text-transparent hover:border-neutral-700 hover:text-neutral-700 dark:hover:border-neutral-300 dark:hover:text-neutral-300"
      >
        <Check size={14} strokeWidth={3} aria-hidden />
      </button>
      <div className="min-w-0 flex-1">
        <button
          type="button"
          onClick={() => ctx.openTask(task.id)}
          className="block w-full truncate text-left text-sm text-neutral-900 hover:text-accent dark:text-neutral-100"
        >
          {nextAction || task.title || t("task.untitled")}
        </button>
        {nextAction && <p className="truncate text-[12px] text-neutral-500">{task.title}</p>}
        {fitMarker}
        {rotating && turnOf && (
          <p className="text-[12px] text-neutral-500" data-testid="turn" data-turn={turnOf}>
            {turnOf === me ? t("circle.yourTurn") : t("circle.turn", { name: nameOf(turnOf) })}
          </p>
        )}
        {isRecurring && (
          <div className="mt-1 flex items-center gap-2">
            <WantBar
              className="w-40"
              value={wantDisplay(want, task.props["rec.ceiling"] as number | undefined)}
              level={wantLevel(want)}
              ceiling={(task.props["rec.ceiling"] as number) || 3}
            />
            <span className="font-mono text-[11px] text-neutral-500">{round1(want)}</span>
            <Why
              title={t("want.label")}
              formula="want = (age / period) ^ elasticity"
              rows={[
                [t("want.age"), `${round1((computed.daysSinceDone as number) ?? 0)} d`],
                [
                  t("props.rec.periodDays"),
                  formatPeriod(t, Number(task.props["rec.periodDays"]) || 7, task.props["rec.periodUnit"]),
                ],
                [t("props.rec.elasticity"), String(task.props["rec.elasticity"] ?? 1)],
              ]}
            />
          </div>
        )}
      </div>
      {groupIds.slice(0, 1).map((gid) => (
        <span key={gid} className="hidden items-center gap-1.5 text-[12px] text-neutral-500 sm:inline-flex">
          <i aria-hidden className={clsx("h-2 w-2 rounded-full", toneDot(groups[gid]?.color))} />
          {groups[gid]?.title}
        </span>
      ))}
      <EnergyPips value={energyOf(task)} label={t("energy.taskLabel", { n: energyOf(task) })} />
      {ctx.profileKind !== "kid" && (
        <button
          type="button"
          aria-label={t("starter.label")}
          title={t("starter.label")}
          disabled={ro}
          className="grid h-[30px] w-[30px] place-items-center rounded-sm text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900 dark:hover:bg-neutral-800 dark:hover:text-neutral-100"
          data-testid="starter"
          onClick={() =>
            ctx.write(() => {
              void startTracking(ctx.doc, ctx.workspaceId, task.id);
              arm(task.id, STARTER_MINUTES);
            })
          }
        >
          <Timer size={15} aria-hidden />
        </button>
      )}
      {!ro && (
        <div ref={menuRef} className="relative">
          <button
            type="button"
            aria-label={t("notNow.menu", { title: task.title })}
            aria-expanded={menuOpen}
            className="grid h-[30px] w-[30px] place-items-center rounded-sm text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900 dark:hover:bg-neutral-800 dark:hover:text-neutral-100"
            onClick={toggleMenu}
            data-testid="bin-row-menu"
          >
            <MoreHorizontal size={15} aria-hidden />
          </button>
          {menuOpen && (
            <Floating
              anchor={menuRef}
              panelRef={menuPanel}
              align="end"
              className="w-60 rounded-md border border-neutral-200 bg-[var(--surface)] p-1 text-sm shadow-[var(--shadow-md)] dark:border-neutral-700"
            >
              <ul role="menu">
                <NotNowChoices
                  task={task}
                  write={ctx.write}
                  onSet={(count) => {
                    setMenuOpen(false);
                    if (shouldAsk(task, count, Date.now())) setAsking(true);
                    else onChanged();
                  }}
                />
                {/* skipping keeps the turn where it is: only where turns are taken (UI cleanup U3) */}
                {isRecurring && rotating && (
                  <li className="mt-1 border-t border-neutral-200 pt-1 dark:border-neutral-800">
                    <button
                      type="button"
                      role="menuitem"
                      className="w-full rounded-sm px-2 py-1 text-left hover:bg-neutral-100 dark:hover:bg-neutral-800"
                      onClick={() => {
                        ctx.write((doc) => skipRecurring(doc, task.id));
                        setMenuOpen(false);
                        onChanged();
                      }}
                    >
                      {t("recurring.skip")}
                    </button>
                  </li>
                )}
              </ul>
            </Floating>
          )}
        </div>
      )}
      {asking && (
        <NotNowQuestion
          task={task}
          shared={shared}
          write={ctx.write}
          openTask={ctx.openTask}
          onClose={() => {
            setAsking(false);
            onChanged();
          }}
        />
      )}
    </li>
  );
}

function toneDot(color: string | null | undefined): string {
  switch (color) {
    case "accent":
      return "bg-accent";
    case "ok":
      return "bg-ok";
    case "warn":
      return "bg-warn";
    case "err":
      return "bg-err";
    default:
      return "bg-neutral-400";
  }
}

/** "Wants doing", with "Show from" (ADR 0024). */
export const BinViewEverywhere = withElsewhere(BinView);
export { BinView };

registerViewType<BinViewConfig>({
  id: "bin",
  label: "views.bin.label",
  icon: "flame",
  level: "simple",
  defaultConfig: {
    sort: [
      { by: "want", dir: "desc" },
      { by: "energy", dir: "asc" },
    ],
    limit: 12,
    includeBlocked: false,
    showQueued: false,
  },
  configSchema: {
    type: "object",
    properties: {
      limit: { type: "number", title: "config.limit", minimum: 1, maximum: 100 },
      includeBlocked: { type: "boolean", title: "config.includeBlocked" },
      showQueued: { type: "boolean", title: "config.showQueued" },
      includeSomeday: { type: "boolean", title: "config.includeSomeday" },
      source: SOURCE_SCHEMA,
      filter: FILTER_SCHEMA,
    },
  },
  Component: BinViewEverywhere,
  minSize: { w: 3, h: 3 },
});
